import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { updateAgreement } from '../../services/agreementsService';
import { useAuth } from '../../context/authContext';
import ImageUploaderWithCrop from '../images/ImageUploaderWithCrop'; // Asegúrate de importar tu componente de recorte
import ModalTitle from '../common/ModalTitle';
import { X } from 'lucide-react';

const EditAgreement = ({ agreement, onClose, onUpdate, setIsSubmitting }) => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // Para la vista previa de la imagen
  const [hasChanges, setHasChanges] = useState(false);

  // Clases de estilo
  const inputClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none shadow-sm";
  const labelClass = "block mb-2 font-medium text-gray-700";
  const errorClass = "text-red-500 text-sm mt-1";
  const buttonClass = "w-full py-3 px-4 rounded-lg transition-all duration-300 font-medium";

  const validationSchema = Yup.object({
    title: Yup.string().required('El título es requerido'),
    description: Yup.string().required('La descripción es requerida'),
    contact: Yup.string().email('Debe ser un email válido').required('El contacto es requerido'),
    image: Yup.mixed().test(
      'fileRequired',
      'La imagen es requerida',
      function (value) {
        return !!value || !!agreement.image;
      }
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: agreement.title || '',
      description: agreement.description || '',
      contact: agreement.contact || '',
      image: selectedFile || agreement.image || null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        onClose();
        const confirmation = await Swal.fire({
          title: '¿Estás seguro?',
          text: '¿Quieres guardar los cambios realizados?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#0488AF',
          cancelButtonColor: '#C42251',
          confirmButtonText: 'Sí, guardar',
          cancelButtonText: 'Cancelar',
        });

        if (!confirmation.isConfirmed) return;

        setIsSubmitting(true);

        const updatedAgreement = {
          ...agreement,
          title: values.title,
          description: values.description,
          contact: values.contact,
          image: selectedFile || agreement.image,
        };

        const response = await updateAgreement(updatedAgreement, user);

        setIsSubmitting(false);

        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Convenio actualizado',
            showConfirmButton: false,
            timer: 1500,
          });
          onUpdate();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error al actualizar el convenio',
            text: response.message,
          });
        }
      } catch (error) {
        setIsSubmitting(false);
        Swal.fire({
          icon: 'error',
          title: 'Error inesperado',
          text: 'Hubo un problema al actualizar el convenio. Inténtalo de nuevo más tarde.',
        });
      }
    },
  });

  useEffect(() => {
    const formValues = {
      title: formik.values.title,
      description: formik.values.description,
      contact: formik.values.contact,
      image: selectedFile || agreement.image || null,
    };

    const originalValues = {
      title: agreement.title,
      description: agreement.description,
      contact: agreement.contact,
      image: agreement.image || null,
    };

    const valuesChanged =
      formValues.title !== originalValues.title ||
      formValues.description !== originalValues.description ||
      formValues.contact !== originalValues.contact ||
      (selectedFile !== null);

    setHasChanges(valuesChanged);
  }, [formik.values, selectedFile, agreement]);

  useEffect(() => {
    setSelectedFile(null);
    formik.setFieldValue('image', agreement.image || null);
  }, []);

  return (
    <div className="flex flex-col">
      {/* Header con título y botón de cerrar */}
      <div className="relative">
          <ModalTitle title="Editar convenio" />
          <div className="absolute top-3 right-3 flex gap-2">
            <button
              type="button"
              onClick={() => onClose()}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>



      {/* Componente de carga y recorte de imagen */}
      

      <div className="p-6">
        <form onSubmit={formik.handleSubmit} className="space-y-5">
        <div>
          <img
            src={agreement.image}
            alt="Imagen del convenio"
            className="w-full h-auto aspect-video object-cover rounded-lg mb-4"
            loading='lazy'
          />
        </div>


          <div>
            <label htmlFor="title" className={labelClass}>Título</label>
            <input
              id="title"
              name="title"
              type="text"
              className={inputClass}
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Título del convenio"
            />
            {formik.touched.title && formik.errors.title && (
              <p className={errorClass}>{formik.errors.title}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className={labelClass}>Descripción</label>
            <textarea
              id="description"
              name="description"
              rows="6"
              className={inputClass}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Describe el convenio y sus beneficios"
            />
            {formik.touched.description && formik.errors.description && (
              <p className={errorClass}>{formik.errors.description}</p>
            )}
          </div>

          <div>
            <label htmlFor="contact" className={labelClass}>Contacto (Email)</label>
            <input
              id="contact"
              name="contact"
              type="email"
              className={inputClass}
              value={formik.values.contact}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="correo@ejemplo.com"
            />
            {formik.touched.contact && formik.errors.contact && (
              <p className={errorClass}>{formik.errors.contact}</p>
            )}
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 mb-6">
            <ImageUploaderWithCrop
              aspectRatio={16 / 9}
              onCropped={(blob) => {
                if (imagePreview) URL.revokeObjectURL(imagePreview);
                const url = URL.createObjectURL(blob);
                setSelectedFile(blob);
                setImagePreview(url);
                formik.setFieldValue("image", blob);
                formik.setFieldTouched("image", true, false);
              }}
              setFieldTouched={formik.setFieldTouched}
              setFieldError={formik.setFieldError}
              setFieldValue={formik.setFieldValue}
              maxFileSize={2 * 1024 * 1024} // 2MB
              fieldName="image"
              values={formik.values}
            />
            <p className="text-sm text-gray-500">Formatos: JPG, PNG. Tamaño máximo: 2MB</p>
            {formik.touched.image && formik.errors.image && (
              <p className={errorClass}>{formik.errors.image}</p>
            )}

            {imagePreview && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Vista previa:</h4>
                <img
                  src={imagePreview}
                  alt="Vista previa"
                  className="rounded-lg shadow-md w-full object-cover h-48"
                  loading='lazy'
                />
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`${buttonClass} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!hasChanges || !formik.isValid}
              className={`${buttonClass} bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white hover:from-[#071f3d] hover:to-[#03779C] disabled:opacity-70`}
            >
              Guardar Cambios
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditAgreement;
